import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Loader2, Search, Globe, Mail, Phone, MapPin, Camera, X, Check, Upload, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  name: '',
  service_offered: '',
  contact_email: '',
  location: '',
  website: '',
  contact_mobile: '',
  photo: '',
  is_active: true
};

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const adminToken = localStorage.getItem('adminToken');
  const axiosConfig = { headers: { Authorization: `Bearer ${adminToken}` } };

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/admin/services`, axiosConfig);
      setServices(response.data.services || []);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, photo: response.data.url }));
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.service_offered.trim()) {
      toast.error('Name and Service Offered are required');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/services/${editingId}`, formData, axiosConfig);
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/admin/services`, formData, axiosConfig);
        toast.success('Service created');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ ...emptyForm });
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (service) => {
    setFormData({
      name: service.name || '',
      service_offered: service.service_offered || '',
      contact_email: service.contact_email || '',
      location: service.location || '',
      website: service.website || '',
      contact_mobile: service.contact_mobile || '',
      photo: service.photo || '',
      is_active: service.is_active !== false
    });
    setEditingId(service.id);
    setShowForm(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API}/admin/services/${serviceId}`, axiosConfig);
      toast.success('Service deleted');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleToggleActive = async (service) => {
    try {
      await axios.put(`${API}/admin/services/${service.id}`, { is_active: !service.is_active }, axiosConfig);
      toast.success(`Service ${service.is_active ? 'deactivated' : 'activated'}`);
      fetchServices();
    } catch (error) {
      toast.error('Failed to update service status');
    }
  };

  const filteredServices = services.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.service_offered?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const inputClass = "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20";

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
        <p className="text-slate-400">Loading Services...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-services">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Service Providers</h1>
          <p className="text-slate-400 mt-1">Manage service providers visible to partners</p>
        </div>
        <Button
          onClick={() => { setShowForm(true); setEditingId(null); setFormData({ ...emptyForm }); }}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
          data-testid="add-service-btn"
        >
          <Plus size={18} className="mr-2" /> Add Service
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Search by name, service, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`pl-10 ${inputClass}`}
          data-testid="search-services"
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl max-w-lg w-full shadow-2xl border border-slate-800 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-800">
              <h2 className="text-xl font-bold text-white">
                {editingId ? 'Edit Service' : 'Add New Service'}
              </h2>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Photo Upload */}
              <div>
                <Label className="text-slate-300 text-sm">Photo / Logo</Label>
                <div className="mt-2 flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center">
                    {formData.photo ? (
                      <img src={formData.photo.startsWith('/') ? `${BACKEND_URL}${formData.photo}` : formData.photo} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-8 h-8 text-slate-600" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      data-testid="upload-photo-btn"
                    >
                      {uploadingPhoto ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
                      {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                    {formData.photo && (
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, photo: '' }))} className="text-red-400 text-xs hover:text-red-300">
                        Remove photo
                      </button>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                </div>
              </div>

              {/* Name */}
              <div>
                <Label className="text-slate-300 text-sm">Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Rajesh Sharma & Associates" className={inputClass} data-testid="service-name-input" required />
              </div>

              {/* Service Offered */}
              <div>
                <Label className="text-slate-300 text-sm">Service Offered *</Label>
                <Input value={formData.service_offered} onChange={(e) => setFormData(prev => ({ ...prev, service_offered: e.target.value }))} placeholder="e.g. Financial Planning" className={inputClass} data-testid="service-offered-input" required />
              </div>

              {/* Contact Email */}
              <div>
                <Label className="text-slate-300 text-sm">Contact Email</Label>
                <Input type="email" value={formData.contact_email} onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))} placeholder="email@example.com" className={inputClass} data-testid="service-email-input" />
              </div>

              {/* Contact Mobile */}
              <div>
                <Label className="text-slate-300 text-sm">Contact Mobile</Label>
                <Input value={formData.contact_mobile} onChange={(e) => setFormData(prev => ({ ...prev, contact_mobile: e.target.value }))} placeholder="+91 98765 43210" className={inputClass} data-testid="service-mobile-input" />
              </div>

              {/* Location */}
              <div>
                <Label className="text-slate-300 text-sm">Location</Label>
                <Input value={formData.location} onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))} placeholder="e.g. Mumbai, Maharashtra" className={inputClass} data-testid="service-location-input" />
              </div>

              {/* Website */}
              <div>
                <Label className="text-slate-300 text-sm">Website</Label>
                <Input value={formData.website} onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))} placeholder="https://example.com" className={inputClass} data-testid="service-website-input" />
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <Label className="text-slate-300 text-sm">Active (visible to partners)</Label>
                <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_active: v }))} data-testid="service-active-switch" />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }} className="flex-1 border-slate-700 text-slate-300">
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" data-testid="save-service-btn">
                  {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Services Table */}
      {filteredServices.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-12 text-center">
            <p className="text-slate-400 text-lg">
              {searchTerm ? 'No services match your search' : 'No services added yet. Click "Add Service" to get started.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left p-4 text-slate-400 text-sm font-medium">Photo</th>
                  <th className="text-left p-4 text-slate-400 text-sm font-medium">Name</th>
                  <th className="text-left p-4 text-slate-400 text-sm font-medium">Service</th>
                  <th className="text-left p-4 text-slate-400 text-sm font-medium">Location</th>
                  <th className="text-left p-4 text-slate-400 text-sm font-medium">Contact</th>
                  <th className="text-left p-4 text-slate-400 text-sm font-medium">Status</th>
                  <th className="text-right p-4 text-slate-400 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.map((service) => (
                  <tr key={service.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                    <td className="p-4">
                      <div className="w-10 h-10 rounded-lg bg-slate-800 overflow-hidden">
                        {service.photo ? (
                          <img src={service.photo.startsWith('/') ? `${BACKEND_URL}${service.photo}` : service.photo} alt={service.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-600 text-sm font-bold">
                            {service.name?.[0]?.toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-white font-medium">{service.name}</span>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/30">
                        {service.service_offered}
                      </span>
                    </td>
                    <td className="p-4 text-slate-400 text-sm">{service.location || '-'}</td>
                    <td className="p-4">
                      <div className="text-sm space-y-1">
                        {service.contact_email && <div className="text-slate-400">{service.contact_email}</div>}
                        {service.contact_mobile && <div className="text-slate-500">{service.contact_mobile}</div>}
                      </div>
                    </td>
                    <td className="p-4">
                      <button onClick={() => handleToggleActive(service)} className="cursor-pointer" data-testid={`toggle-active-${service.id}`}>
                        {service.is_active ? (
                          <span className="flex items-center gap-1 text-emerald-400 text-xs"><Eye size={12} /> Active</span>
                        ) : (
                          <span className="flex items-center gap-1 text-slate-500 text-xs"><EyeOff size={12} /> Inactive</span>
                        )}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(service)} className="text-slate-400 hover:text-amber-400" data-testid={`edit-service-${service.id}`}>
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(service.id)} className="text-slate-400 hover:text-red-400" data-testid={`delete-service-${service.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServices;
